<template>
    <div class="ebitda-development" v-if="current !== -1">
        <div class="title">Akkumuleret EBITDA i regnskabsåret</div>

        <vue-apex-charts type="bar" :height="300" :series="series" :options="options" />

        <div class="legend">
            <span><i :style="{ background: colors.budget }" />Budget</span>
            <span><i class="forecast" />Forventet</span>
        </div>

        <div class="note" v-if="wip && wip.total">
            Starter fra realiseret EBITDA i regnskabsåret indtil i dag. Værdi af opgaver for {{ wip.total | currency }} indgår: afsluttede, ikke fakturerede i {{ months[current].month | moment('MMMM') }}, igangværende fordelt jævnt til og med {{ months[months.length - 1].month | moment('MMMM') }}
        </div>
    </div>
</template>

<style lang="scss" scoped>
    .ebitda-development {
        .title {
            font-weight: 700;
            margin-bottom: 5px;
        }

        .legend {
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            gap: 5px 20px;
            font-size: 13px;

            i {
                display: inline-block;
                width: 12px;
                height: 12px;
                border-radius: 2px;
                margin-right: 6px;
                vertical-align: -1px;
                box-sizing: border-box;

                &.forecast {
                    background: rgba(76, 175, 80, 0.35);
                    border: 2px dashed #4CAF50;
                }
            }
        }

        .note {
            margin-top: 8px;
            font-size: 12px;
            color: #888;
            text-align: center;
        }
    }
</style>

<script>
import VueApexCharts from 'vue-apexcharts'

const DASH = '4 3'

export default {
    props: {
        months: Array,
        wip: Object
    },
    data () {
        return {
            colors: {
                budget: '#172b43',
                positive: '#4CAF50',
                negative: '#FF5252',
                positiveFaded: 'rgba(76, 175, 80, 0.35)',
                negativeFaded: 'rgba(255, 82, 82, 0.35)'
            }
        }
    },
    computed: {
        current () {
            return this.months.findIndex(month => month.forecast !== null)
        },
        remaining () {
            return this.months.slice(this.current)
        },
        labels () {
            return this.remaining.map(month => this.$options.filters.moment(month.month, 'MMM'))
        },
        realizedToDate () {
            return this.months.slice(0, this.current + 1).reduce((sum, month) => sum + (month.realized || 0), 0)
        },
        // Finished but uninvoiced projects are invoiced now, ongoing ones over the rest of the year.
        // Booking it all in the current month would show a jump that is only a catch-up of past months.
        wipPerMonth () {
            const ongoing = ((this.wip && this.wip.ongoing) || 0) / this.remaining.length
            const unbilled = (this.wip && this.wip.unbilled) || 0
            return this.remaining.map((month, index) => ongoing + (index === 0 ? unbilled : 0))
        },
        // Both start from what is realized so far and get the same WIP,
        // so the two end exactly the budget deviation apart.
        forecastAccumulated () {
            return this.accumulate(month => month.forecast)
        },
        budgetAccumulated () {
            return this.accumulate(month => month.budget_rest)
        },
        series () {
            return [
                {
                    name: 'Budget',
                    data: this.remaining.map((month, index) => ({ x: this.labels[index], y: Math.round(this.budgetAccumulated[index]) }))
                },
                {
                    name: 'Forventet',
                    data: this.remaining.map((month, index) => {
                        const value = this.forecastAccumulated[index]
                        const positive = value >= 0
                        return {
                            x: this.labels[index],
                            y: Math.round(value),
                            fillColor: positive ? this.colors.positiveFaded : this.colors.negativeFaded,
                            strokeColor: positive ? this.colors.positive : this.colors.negative
                        }
                    })
                }
            ]
        },
        options () {
            return {
                colors: [this.colors.budget, this.colors.positive],
                plotOptions: {
                    bar: {
                        columnWidth: '70%'
                    }
                },
                fill: {
                    opacity: 1
                },
                stroke: {
                    show: true,
                    width: 2,
                    colors: ['transparent']
                },
                dataLabels: {
                    enabled: false
                },
                legend: {
                    show: false
                },
                yaxis: {
                    labels: {
                        formatter: value => this.$options.filters.currency(value)
                    },
                    forceNiceScale: true
                },
                chart: {
                    toolbar: {
                        show: false
                    },
                    zoom: {
                        enabled: false
                    },
                    events: {
                        mounted: chart => this.dashForecast(chart),
                        updated: chart => this.dashForecast(chart)
                    }
                },
                tooltip: {
                    shared: true,
                    intersect: false,
                    y: {
                        formatter: value => value === null || value === undefined ? '-' : this.$options.filters.currency(value),
                        title: {
                            formatter: name => name + ': '
                        }
                    }
                }
            }
        }
    },
    methods: {
        accumulate (amount) {
            let sum = this.realizedToDate
            return this.remaining.map((month, index) => {
                sum += (amount(month) || 0) + this.wipPerMonth[index]
                return sum
            })
        },
        // apexcharts 3.24 has no per-point dash, so the forecast bars get it on the rendered SVG.
        dashForecast (chart) {
            chart.el.querySelectorAll('path.apexcharts-bar-area[index="1"]').forEach(path => {
                path.setAttribute('stroke-dasharray', DASH)
            })
        }
    },
    components: {
        VueApexCharts
    }
}
</script>



// WEBPACK FOOTER //
// EbitdaDevelopment.vue?6f09910b